import type { ECElementEvent } from 'echarts';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import { isNumber, sum } from 'lodash-es';
import type { JSX } from 'react';

import { useStableChartOption } from './StableChartOption';
import { ChartAxis, ChartSeries, axisColor, chartColor, chartFont } from './charts.common';

export interface StackedAreaChartProps {
    onChartClick: (item: string) => void;
    xAxisLabels: string[];
    /** `name` must be provided. */
    yAxisInfo: ChartAxis;
    /** The lines of the graph. The area below each line will be shaded with the line's `color`. The length of this array must equal the length of `xAxisLabels`. */
    lines: ChartSeries[];
}

/**
 * Shows multiple changing values over time. Similar to a line chart with multiple lines, except that the area below each line is shaded and each given point in time (the x-axis is time) shows the sum of each of the line's values (the y-axis is a total).
 */
export const StackedAreaChart = (props: StackedAreaChartProps): JSX.Element => {
    const primaryFont = getComputedStyle(document.documentElement).getPropertyValue(chartFont);
    const labelColor = getComputedStyle(document.documentElement).getPropertyValue(chartColor);

    const ariaDescription = (() => {
        const getValueAsNumber = (xAxisIndex: number, line: ChartSeries): number => {
            const dataPoint = line.data[xAxisIndex];
            return isNumber(dataPoint) ? dataPoint : dataPoint.value;
        };

        const getLineDescriptions = (xAxisIndex: number): string => {
            return props.lines.map((line) => `${getValueAsNumber(xAxisIndex, line)} (${line.name})`).join(', ');
        };

        const getTotal = (xAxisIndex: number) => sum(props.lines.map((line) => getValueAsNumber(xAxisIndex, line)));

        const valuesDescriptions = props.xAxisLabels.map((xAxisLabel, xAxisIndex) => `For ${xAxisLabel}, the values of the lines are ${getLineDescriptions(xAxisIndex)}; and the total (${props.yAxisInfo.name!}) is ${getTotal(xAxisIndex)}.`).join(' ');
        return `A stacked area chart showing ${props.yAxisInfo.name!} with ${props.lines.length} lines. ${valuesDescriptions}`;
    })();

    const getOption = (info: Omit<StackedAreaChartProps, 'onChartClick'>, primaryFont: string, labelColor: string): EChartsOption => {
        // Add a non functional axis to complete the grid
        const yAxes: ChartAxis[] = [info.yAxisInfo, { name: '', position: 'right' }];

        return {
            tooltip: {
                trigger: 'axis',
            },
            legend: { show: false },
            textStyle: {
                fontFamily: primaryFont,
            },
            grid: {
                containLabel: true,
            },
            xAxis: [
                {
                    type: 'category',
                    axisTick: {
                        alignWithLabel: true,
                    },
                    data: info.xAxisLabels.map((label) => label.toUpperCase()),
                    axisLine: {
                        show: true,
                        lineStyle: {
                            color: axisColor,
                        },
                    },
                    boundaryGap: false,
                    axisLabel: {
                        color: labelColor,
                    },
                },
            ],

            yAxis: yAxes.map((yAxis) => ({
                type: 'value',
                name: yAxis.name?.toUpperCase(),
                position: yAxis.position,
                min: yAxis.min,
                max: yAxis.max,
                minInterval: yAxis.minInterval,
                maxInterval: yAxis.maxInterval,
                interval: yAxis.interval,
                alignTicks: true,
                show: true,
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: axisColor,
                    },
                },
                axisLabel: {
                    formatter: yAxis.labelFormatter,
                    color: labelColor,
                },
                nameLocation: 'middle',
                nameGap: 50,
                nameRotate: yAxis.position === 'left' ? 90 : 270,
                splitLine: {
                    lineStyle: {
                        color: axisColor,
                    },
                },
                nameTextStyle: {
                    color: labelColor,
                },
            })),
            series: info.lines.map((lineInfo) => ({
                name: lineInfo.name,
                type: 'line',
                stack: 'Total',
                areaStyle: {},
                emphasis: {
                    focus: 'series',
                },
                itemStyle: {
                    color: lineInfo.color,
                },
                symbol: 'circle',
                symbolSize: 10,
                data: lineInfo.data,
            })),
        };
    };

    const option = useStableChartOption(getOption(props, primaryFont, labelColor));

    return (
        <ReactECharts
            aria-label={ariaDescription}
            role="img"
            option={option}
            style={{
                height: '100%',
                width: '100%',
                minHeight: '400px',
                minWidth: '400px',
            }}
            onEvents={{ click: (params: ECElementEvent) => props.onChartClick(`${params.name} | ${params.seriesName}`) }}
        />
    );
};
